import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, SESSION_LOST_EVENT } from '~/lib/api-client';
import type { AuthMeResponse } from '~/lib/use-auth-gate';
import { clearDesktopComposers, mountDesktopComposerHost } from './desktop-composer-bridge';

/** Owns the public composer host and cancels work when account context changes. */
export function useDesktopComposerHost(open: (session: string) => void) {
  const queryClient = useQueryClient();
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    const identityHint = () => {
      const me = queryClient.getQueryData<AuthMeResponse>(['auth', 'me']);
      const tenant = queryClient.getQueryData<{ settings?: { zitadelOrgId?: string } }>(['tenants', 'current']);
      const switched = queryClient.getQueryData<{ current?: string }>(['tenants', 'switchable']);
      // Cached values only invalidate an existing binding. Optional settings
      // never supply or gate the authenticated computer owner.
      return [me?.authenticated, me?.userId, tenant?.settings?.zitadelOrgId, switched?.current] as const;
    };
    let owner: string | null = null;
    let disposeHost = () => {};
    let stopped = false;
    let pending: AbortController | null = null;
    let hint = identityHint();

    const dispose = () => {
      stopped = true;
      pending?.abort();
      disposeHost();
      clearDesktopComposers(document);
    };

    const reconcile = async (ownerChanged = false) => {
      if (stopped) return;
      if (queryClient.getQueryData<AuthMeResponse>(['auth', 'me'])?.authenticated === false) {
        dispose();
        return;
      }
      // A changed account while first-owner discovery is pending cannot adopt
      // registrations captured under that earlier, still-unverified account.
      if (pending && !owner && ownerChanged) clearDesktopComposers(document);
      pending?.abort();
      disposeHost();
      disposeHost = () => {};
      const request = new AbortController();
      pending = request;
      try {
        const actual = await api.get<{ orgId: string; userId: string }>('/api/machines/desktop/identity', request.signal);
        if (stopped || request.signal.aborted || pending !== request) return;
        const me = queryClient.getQueryData<AuthMeResponse>(['auth', 'me']);
        if (
          !actual || typeof actual.orgId !== 'string' || !actual.orgId ||
          typeof actual.userId !== 'string' || !actual.userId ||
          me?.userId && me.userId !== actual.userId
        ) {
          throw Error('Desktop owner changed');
        }
        owner = JSON.stringify([document.location.origin, actual.orgId, actual.userId]);
        disposeHost = mountDesktopComposerHost(document, session => openRef.current(session), owner);
      } catch {
        if (!stopped && !request.signal.aborted && pending === request) clearDesktopComposers(document);
      } finally {
        if (pending === request) pending = null;
      }
    };

    void reconcile();
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const next = identityHint();
      if (!next.some((value, index) => value !== hint[index])) return;
      // Initial cache population supplies missing context; only a change to
      // previously known identity is evidence that pending inputs belong to
      // another owner. Still refresh live identity and cancel the old request.
      const knownOrg = hint[3] ?? hint[2];
      const ownerChanged = hint.some((value, index) => value !== undefined && value !== next[index]) ||
        knownOrg !== undefined && [next[2], next[3]].some(org => org !== undefined && org !== knownOrg);
      hint = next;
      void reconcile(ownerChanged);
    });
    window.addEventListener(SESSION_LOST_EVENT, dispose);
    window.addEventListener('pagehide', dispose);
    return () => {
      unsubscribe();
      window.removeEventListener(SESSION_LOST_EVENT, dispose);
      window.removeEventListener('pagehide', dispose);
      dispose();
    };
  }, [queryClient]);
}
